import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense, useEffect } from 'react';
import PageLoadingState from './components/PageLoadingState';
import HomeLayout from './components/HomeLayout';
import { HomeFeatureFlagProvider } from './context/HomeFeatureFlagContext';
import { MapProvider } from './context/MapContext';
import { DialogProvider } from './context/DialogContext';
import { ModalProvider, useModals } from './context/ModalContext';
import { RouteEditorProvider, useRouteEditor } from './context/RouteEditorContext';
import { BftRoutesProvider } from './context/BftRoutesContext';
import { LayerControlProvider } from './context/LayerControlContext';
import { UploadQueueProvider } from './context/UploadQueueContext';
import ModalManager from './components/modals/ModalManager';
import UploadProgressPanel from './components/UploadProgressPanel';
import { initializePopupRenderer } from './utils/popupRenderer';
import { registerModalsAPI } from './utils/modalsBridge';
import { initializeLayerAdapter } from './hooks/useLayerCatalog';
import { initializePathFinding } from './hooks/usePathFinding';
import { registerLayerControlBridge } from './utils/layerControlBridge';
import { initializePiaStyles } from './hooks/usePiaStyles';
import { useOrientationResize } from './hooks/useOrientationResize';
import './styles/home.css';
import './styles/marker-creation-popup.css';
import './styles/structure-type-selector.css';
import './styles/structure-form.css';
const MapWorkspace = React.lazy(() => import('./screens/MapWorkspace'));
const HomeAppInner = () => {
    useOrientationResize();
    const modals = useModals();
    const routeEditor = useRouteEditor();
    // Initialize popup renderer, layer adapter, path finding, and bridges on mount
    useEffect(() => {
        // Initialize LayerAdapter for backward compatibility with legacy code
        console.log('[HomeApp] Initializing LayerAdapter...');
        initializeLayerAdapter();
        // Initialize CFN.getPath for backward compatibility with legacy code
        console.log('[HomeApp] Initializing PathFinding (CFN.getPath)...');
        initializePathFinding();
        console.log('[HomeApp] Initializing popup renderer...');
        initializePopupRenderer();
        console.log('[HomeApp] Popup renderer initialized. window.CFN.renderModernPopup:', typeof window.CFN?.renderModernPopup);
        // Order popup now handled by WmsFeatureInfoHandler via feature_info_config
        // Register layer control bridge for legacy code
        console.log('[HomeApp] Registering layer control bridge...');
        registerLayerControlBridge();
        // Initialize PIA styling functions for backward compatibility
        console.log('[HomeApp] Initializing PIA styles (CFN.PIAStyle, etc.)...');
        initializePiaStyles();
        // Register modals API for legacy code
        console.log('[HomeApp] Registering modals API...');
        registerModalsAPI({
            uploads: {
                open: modals.openUploadsModal,
                close: modals.closeUploadsModal,
            },
            layerSelector: {
                open: modals.openLayerSelectorModal,
                close: modals.closeLayerSelectorModal,
            },
            routes: {
                open: modals.openRoutesModal,
                close: modals.closeRoutesModal,
            },
            ductView: {
                open: modals.openDuctViewModal,
                close: modals.closeDuctViewModal,
            },
            ductSearch: {
                open: modals.openDuctSearchModal,
                close: modals.closeDuctSearchModal,
            },
            polygonPopup: {
                open: modals.openPolygonPopup,
                close: modals.closePolygonPopup,
            },
            // Route editor modal
            routeEditor: {
                open: modals.openRouteEditorModal,
                close: modals.closeRouteEditorModal,
            },
            // Confirm dialog (replaces areYouSure/quickYesCancel)
            confirm: {
                open: modals.openConfirmDialog,
                close: modals.closeConfirmDialog,
            },
            // Info dialog (replaces infoBox)
            info: {
                open: modals.openInfoDialog,
                close: modals.closeInfoDialog,
            },
            // Conduit create modal (for new conduit creation from map drawing)
            conduitCreate: {
                open: modals.openConduitCreateModal,
                close: modals.closeConduitCreateModal,
            },
        });
        // Expose route editor API to window.CFN for any legacy code that needs it
        console.log('[HomeApp] Exposing route editor API to window.CFN...');
        const CFN = window.CFN || {};
        window.CFN = CFN;
        CFN.routeEditor = {
            openModal: modals.openRouteEditorModal,
            openRoutes: modals.openRoutesModal,
            toggleEditMode: routeEditor.toggleEditMode,
            clearRoute: routeEditor.clearDucts,
            isEditing: () => routeEditor.isEditing,
        };
    }, [modals, routeEditor]);
    // Note: HomeApp is now rendered in a separate React root (see map-root.tsx)
    // and doesn't use React Router. It only has one view - the map workspace.
    return (_jsxs(_Fragment, { children: [_jsx(ModalManager, {}), _jsx(UploadProgressPanel, {}), _jsx(Suspense, { fallback: _jsx(PageLoadingState, {}), children: _jsx(HomeLayout, { children: _jsx(MapWorkspace, {}) }) })] }));
};
const HomeApp = () => {
    // Note: ThemeProvider is already provided by app-shell.tsx
    // Do not add another ThemeProvider here to avoid nested providers
    return (_jsx(HomeFeatureFlagProvider, { children: _jsx(MapProvider, { children: _jsx(DialogProvider, { children: _jsx(ModalProvider, { children: _jsx(LayerControlProvider, { children: _jsx(RouteEditorProvider, { children: _jsx(BftRoutesProvider, { children: _jsx(UploadQueueProvider, { children: _jsx(HomeAppInner, {}) }) }) }) }) }) }) }) }));
};
export default HomeApp;
